import React, { ReactNode, useState } from 'react'

import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import backgroundImg from '@/assets/images/background.jpg'
import logoImageWhite from '@/assets/images/logos/login.logo-white.png'
import logoImageDark from '@/assets/images/logos/login.logo.png'
import { useCustomSettings } from '@/core/hooks/misc/useCustomSettings'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { EnvironmentIndicator } from '@/shared/components/atoms/EnvironmentIndicator'
import { Link } from '@/shared/components/atoms/Link'
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal/ConfirmationModal'

import { LanguageSelector } from './LanguageSelector'

interface OnboardingLayoutProps {
	children: ReactNode
	hideImageOnStep?: boolean
	/**
	 * When true, clicking the logo to leave (navigate to "/")
	 * should ask for confirmation to avoid losing progress.
	 */
	shouldBlockExit?: boolean
}

export const OnbardingLayout: React.FC<OnboardingLayoutProps> = ({
	children,
	hideImageOnStep = true,
	shouldBlockExit = false,
}) => {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
	const { theme } = useSelector((state: RootState) => state.ui)
	const { data: customSettings } = useCustomSettings()
	const defaultLogo = theme === 'light' ? logoImageDark : logoImageWhite
	const logoSrc = customSettings?.images?.login_logo || defaultLogo
	const backgroundImage = customSettings?.images?.login_bg || backgroundImg

	return (
		<Container>
			<LogoContainer>
				<Link
					to="/"
					onClick={(event) => {
						event.preventDefault()
						event.stopPropagation()

						if (!shouldBlockExit) {
							navigate('/')
							return
						}

						setShowUnsavedChangesModal(true)
					}}
				>
					<img
						src={logoSrc}
						alt="Logo"
						width="140"
						height="auto"
						className="transition-all duration-200"
					/>
				</Link>
				{/* Language selector */}
				<LanguageSelector absolute={false} />
			</LogoContainer>
			<SplitView $hideImage={hideImageOnStep}>
				{!hideImageOnStep && (
					<ImageSection $backgroundImage={backgroundImage}>
						<BrandMessage>
							<BrandHeading>{t('app.tagline')}</BrandHeading>
						</BrandMessage>
					</ImageSection>
				)}
				<ContentSection $fullWidth={hideImageOnStep}>
					<FormContainer>{children}</FormContainer>
				</ContentSection>
			</SplitView>

			{/* Show environment indicator */}
			<EnvironmentIndicator />

			<ConfirmationModal
				visible={showUnsavedChangesModal}
				title={t('common.unsavedChanges')}
				description={t('common.unsavedChanges')}
				confirmText={t('actions.continue')}
				cancelText={t('actions.cancel')}
				variant="default"
				onConfirm={() => {
					setShowUnsavedChangesModal(false)
					navigate('/')
				}}
				onClose={() => setShowUnsavedChangesModal(false)}
			/>
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: 100vh;
	height: 100dvh;
	width: 100%;
	background-color: var(--background);
	position: relative;
	overflow: hidden;
`

const SplitView = styled.div<{ $hideImage: boolean }>`
	display: flex;
	width: 100%;
	flex: 1;
	min-height: 0;
	overflow: hidden;
	padding: ${({ $hideImage }) => ($hideImage ? '0' : '1rem')};

	@media (max-width: 768px) {
		padding: 0;
	}
`

const ContentSection = styled.div<{ $fullWidth: boolean }>`
	flex: 1;
	display: flex;
	flex-direction: column;
	padding: ${({ $fullWidth }) => ($fullWidth ? '0' : '1rem 2rem')};
	background-color: var(--background);
	width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
	overflow-y: auto;

	@media (max-width: 768px) {
		width: 100%;
		padding: 1rem;
	}
`

const LogoContainer = styled.div`
	padding: 1rem 1rem 0;
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
`

const FormContainer = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	max-width: 600px;
	margin: 50px auto;
	width: 100%;
	min-height: 0;

	@media (max-width: 768px) {
		margin: 1rem auto;
	}
`

const ImageSection = styled.div<{ $backgroundImage: string }>`
	flex: 1;
	flex-shrink: 0;
	min-height: 100%;
	background-image: url('${(props) => props.$backgroundImage}');
	background-size: cover;
	background-position: center;
	position: relative;
	border-radius: 12px;
	overflow: hidden;

	&:before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0, 0, 0, 0.5);
	}

	@media (max-width: 768px) {
		display: none;
	}
`

const BrandMessage = styled.div`
	position: absolute;
	bottom: 10%;
	left: 10%;
	color: white;
`

const BrandHeading = styled.h1`
	font-size: 2.5rem;
	font-weight: bold;
	margin: 0;
	line-height: 1.2;
`
